import React, { useState, useEffect } from 'react';

/**
 * MetricsView - Vista estilo panel de métricas con sparklines
 * Métricas agrupadas por dispositivo con líneas de tendencia suaves
 */
const MetricsView = ({
  devices,
  sites,
  user,
  viewPreferences = {},
  onOpenHistory,
  onOpenGaugePanel,
  getDeviceStatus,
  getDeviceValues,
  getValueStatus,
  theme = "dark",
  themeColors = null
}) => {
  // Colores del tema por defecto si no se pasan
  const colors = themeColors || (theme === "dark" ? {
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
    cardBg: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
    text: "#ffffff",
    textSecondary: "rgba(255,255,255,0.6)",
    textMuted: "rgba(255,255,255,0.4)",
    border: "rgba(255,255,255,0.1)",
    valueBg: "rgba(255,255,255,0.05)"
  } : {
    background: "linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 50%, #f0f4f8 100%)",
    cardBg: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
    text: "#1e293b",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "rgba(0,0,0,0.1)",
    valueBg: "rgba(0,0,0,0.05)"
  });
  // Estado para histórico de valores (para sparklines)
  const [valueHistory, setValueHistory] = useState({});

  // Acumular valores históricos para sparklines
  useEffect(() => {
    if (!devices || devices.length === 0) return;

    setValueHistory(prev => {
      const newHistory = { ...prev };

      devices.forEach(device => {
        if (!device.magnitudes) return;

        device.magnitudes.forEach(mag => {
          const key = `${device._id}_${mag.name}`;
          const value = mag.currentValue;

          if (value !== null && value !== undefined && typeof value === 'number') {
            if (!newHistory[key]) {
              newHistory[key] = [];
            }
            // Mantener últimos 30 valores para el sparkline
            newHistory[key] = [...newHistory[key], value].slice(-30);
          }
        });
      });

      return newHistory;
    });
  }, [devices]);

  // Filtrar dispositivos según permisos del usuario
  const getFilteredDevices = () => {
    if (!devices || devices.length === 0) return [];

    if (!user || user.role === 'admin') {
      return devices;
    }

    return devices.filter(device => {
      if (!user.devicePermissions) return false;
      const devicePermissions = user.devicePermissions[device._id];
      return devicePermissions && devicePermissions.allowed;
    });
  };

  const filteredDevices = getFilteredDevices();

  // Filtrar sitios que tienen dispositivos visibles para el usuario
  const getFilteredSites = () => {
    if (!sites || sites.length === 0) return [];
    const deviceSiteIds = new Set(filteredDevices.map(d => d.site?._id || d.siteId).filter(Boolean));
    return sites.filter(site => deviceSiteIds.has(site._id));
  };

  const filteredSites = getFilteredSites();

  // Calcular estadísticas globales
  const getGlobalStats = () => {
    const totalDevices = filteredDevices.length;
    const onlineDevices = filteredDevices.filter(d => getDeviceStatus(d) === 'online').length;
    const offlineDevices = totalDevices - onlineDevices;
    const totalSites = filteredSites.length;

    let alertCount = 0;
    filteredDevices.forEach(device => {
      const values = getDeviceValues(device);
      values.forEach(v => {
        if (getValueStatus(v) === 'danger') alertCount++;
      });
    });

    return { totalDevices, onlineDevices, offlineDevices, totalSites, alertCount };
  };

  // Sparkline mejorado con curva suave tipo Bezier
  const Sparkline = ({ data, color = '#6366f1', width = 120, height = 40 }) => {
    if (!data || data.length < 2) {
      return (
        <svg width={width} height={height} style={{ overflow: 'visible' }}>
          <line x1="0" y1={height/2} x2={width} y2={height/2}
                stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4,4" />
        </svg>
      );
    }

    const padding = 2;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    // Calcular puntos
    const points = data.map((value, index) => ({
      x: padding + (index / (data.length - 1)) * (width - padding * 2),
      y: padding + (1 - (value - min) / range) * (height - padding * 2)
    }));

    // Crear path con curvas Bezier suaves
    const createSmoothPath = (pts) => {
      if (pts.length < 2) return '';

      let path = `M ${pts[0].x},${pts[0].y}`;

      for (let i = 0; i < pts.length - 1; i++) {
        const current = pts[i];
        const next = pts[i + 1];
        const prev = pts[i - 1] || current;
        const afterNext = pts[i + 2] || next;

        // Control points para curva suave
        const cp1x = current.x + (next.x - prev.x) / 6;
        const cp1y = current.y + (next.y - prev.y) / 6;
        const cp2x = next.x - (afterNext.x - current.x) / 6;
        const cp2y = next.y - (afterNext.y - current.y) / 6;

        path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${next.x},${next.y}`;
      }

      return path;
    };

    const linePath = createSmoothPath(points);
    const areaPath = `${linePath} L ${points[points.length - 1].x},${height} L ${points[0].x},${height} Z`;

    const gradientId = `gradient-${color.replace('#', '')}-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <svg width={width} height={height} style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.5" />
            <stop offset="100%" stopColor={color} stopOpacity="0.1" />
          </linearGradient>
        </defs>
        {/* Área bajo la curva */}
        <path
          d={areaPath}
          fill={`url(#${gradientId})`}
        />
        {/* Línea principal */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Punto final */}
        <circle
          cx={points[points.length - 1].x}
          cy={points[points.length - 1].y}
          r="4"
          fill={color}
        />
        <circle
          cx={points[points.length - 1].x}
          cy={points[points.length - 1].y}
          r="6"
          fill={color}
          opacity="0.3"
        />
      </svg>
    );
  };

  // Tarjeta de estadística global
  const StatCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{
      background: colors.cardBg,
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: `1px solid ${colors.border}`,
      padding: '1.25rem',
      minWidth: '140px',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.75rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        <span style={{ fontSize: '1.25rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '2rem', fontWeight: '700', color: color || colors.text, marginBottom: '0.25rem' }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.7rem', color: colors.textMuted }}>
          {subtitle}
        </div>
      )}
    </div>
  );

  // Tarjeta de métrica individual (compacta)
  const MetricCard = ({ magnitude, historyKey, color }) => {
    const value = magnitude.currentValue;
    const hasValue = value !== null && value !== undefined;
    const history = valueHistory[historyKey] || [];

    let displayValue = '--';
    if (hasValue) {
      if (magnitude.valueType === 'boolean') {
        displayValue = value ? 'ON' : 'OFF';
      } else if (typeof value === 'number') {
        const decimals = magnitude.decimals ?? 1;
        displayValue = value.toFixed(decimals);
      } else {
        displayValue = String(value);
      }
    }

    // Determinar color del valor
    let valueColor = color;
    if (hasValue && typeof value === 'number') {
      const hasMin = magnitude.minNormalValue !== null && magnitude.minNormalValue !== undefined;
      const hasMax = magnitude.maxNormalValue !== null && magnitude.maxNormalValue !== undefined;

      if ((hasMin && value < magnitude.minNormalValue) || (hasMax && value > magnitude.maxNormalValue)) {
        valueColor = '#ef4444';
      }
    }
    if (magnitude.valueType === 'boolean' && hasValue) {
      valueColor = value ? '#10b981' : '#ef4444';
    }

    return (
      <div style={{
        background: colors.valueBg,
        borderRadius: '12px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        minWidth: '180px',
        flex: '1 1 180px',
        transition: 'all 0.3s ease'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>{magnitude.icon || '📊'}</span>
          <span style={{
            fontSize: '0.75rem',
            color: colors.textSecondary,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {magnitude.label}
          </span>
        </div>

        {/* Valor y Sparkline */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '1.5rem', fontWeight: '700', color: valueColor }}>
              {displayValue}
            </span>
            {magnitude.unit && hasValue && magnitude.valueType !== 'boolean' && (
              <span style={{ fontSize: '0.8rem', color: colors.textMuted, marginLeft: '0.25rem' }}>
                {magnitude.unit}
              </span>
            )}
          </div>
          <Sparkline data={history} color={color} width={80} height={32} />
        </div>

        {/* Rango */}
        {magnitude.minNormalValue !== null && magnitude.maxNormalValue !== null && (
          <div style={{ fontSize: '0.65rem', color: colors.textMuted }}>
            Rango: {magnitude.minNormalValue} - {magnitude.maxNormalValue} {magnitude.unit || ''}
          </div>
        )}
      </div>
    );
  };

  // Barra de progreso circular
  const CircularProgress = ({ value, max, color, size = 56 }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={colors.border} strokeWidth="5" />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="5"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
    );
  };

  // Obtener métricas agrupadas por dispositivo
  const getMetricsByDevice = () => {
    const deviceMetrics = [];

    filteredDevices.forEach(device => {
      if (!device.magnitudes) return;

      let allowedMagnitudes = device.magnitudes.filter(mag => mag.showInDashboard !== false);

      // Filtrar por permisos de magnitudes
      if (user && user.role !== 'admin' && user.devicePermissions) {
        const devicePermissions = user.devicePermissions[device._id];
        if (devicePermissions && devicePermissions.magnitudes) {
          allowedMagnitudes = allowedMagnitudes.filter(mag =>
            devicePermissions.magnitudes.includes(mag.name)
          );
        }
      }

      // Filtrar por preferencias de visualización
      const userPrefs = viewPreferences[device._id];
      if (userPrefs !== undefined && Array.isArray(userPrefs)) {
        if (userPrefs.length === 0) {
          allowedMagnitudes = [];
        } else {
          allowedMagnitudes = allowedMagnitudes.filter(mag => userPrefs.includes(mag.name));
        }
      }

      if (allowedMagnitudes.length > 0) {
        deviceMetrics.push({
          device,
          magnitudes: allowedMagnitudes
        });
      }
    });

    return deviceMetrics;
  };

  const stats = getGlobalStats();
  const deviceMetrics = getMetricsByDevice();

  // Colores para las métricas (colores claros para mejor contraste con fondo oscuro)
  const metricColors = ['#a5b4fc', '#c4b5fd', '#67e8f9', '#6ee7b7', '#fcd34d', '#f9a8d4', '#5eead4', '#fdba74'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Estadísticas Globales */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem'
      }}>
        <StatCard
          title="Online"
          value={stats.onlineDevices}
          subtitle={`de ${stats.totalDevices} dispositivos`}
          icon="🟢"
          color="#10b981"
        />
        <StatCard
          title="Offline"
          value={stats.offlineDevices}
          subtitle={stats.offlineDevices > 0 ? "Requiere atención" : "Todo operativo"}
          icon="🔴"
          color={stats.offlineDevices > 0 ? "#ef4444" : "#6b7280"}
        />
        <StatCard
          title="Sitios"
          value={stats.totalSites}
          subtitle="Ubicaciones"
          icon="📍"
          color="#8b5cf6"
        />
        <StatCard
          title="Alertas"
          value={stats.alertCount}
          subtitle={stats.alertCount > 0 ? "Fuera de rango" : "Sin alertas"}
          icon="⚠️"
          color={stats.alertCount > 0 ? "#f59e0b" : "#10b981"}
        />

        {/* Indicador de conectividad */}
        <div style={{
          background: colors.cardBg,
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          transition: 'all 0.3s ease'
        }}>
          <CircularProgress value={stats.onlineDevices} max={stats.totalDevices} color="#10b981" size={48} />
          <div>
            <div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase' }}>Conectividad</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors.text }}>
              {stats.totalDevices > 0 ? Math.round((stats.onlineDevices / stats.totalDevices) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Métricas por Dispositivo */}
      {deviceMetrics.length === 0 ? (
        <div style={{
          background: colors.valueBg,
          borderRadius: '16px',
          padding: '3rem',
          textAlign: 'center',
          color: colors.textMuted,
          transition: 'all 0.3s ease'
        }}>
          No hay métricas disponibles para mostrar
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {deviceMetrics.map(({ device, magnitudes }, deviceIndex) => {
            const status = getDeviceStatus(device);
            const isOnline = status === 'online';
            const isWarning = status === 'warning';
            const mvAccent = isWarning
              ? { primary: '#f59e0b', cardBg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(120, 53, 15, 0.1) 100%)', border: '2px solid rgba(245, 158, 11, 0.6)', shadow: '0 4px 20px rgba(245, 158, 11, 0.2)', badgeBg: 'rgba(245, 158, 11, 0.2)', label: 'Warning' }
              : isOnline
                ? { primary: '#10b981', cardBg: colors.cardBg, border: `1px solid ${colors.border}`, shadow: 'none', badgeBg: 'rgba(16, 185, 129, 0.2)', label: 'Online' }
                : { primary: '#ef4444', cardBg: `linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(127, 29, 29, 0.1) 100%)`, border: '2px solid rgba(239, 68, 68, 0.5)', shadow: '0 4px 20px rgba(239, 68, 68, 0.2)', badgeBg: 'rgba(239, 68, 68, 0.2)', label: 'Offline' };
            const baseColor = metricColors[deviceIndex % metricColors.length];

            return (
              <div
                key={device._id}
                style={{
                  background: mvAccent.cardBg,
                  backdropFilter: 'blur(20px)',
                  borderRadius: '16px',
                  border: mvAccent.border,
                  boxShadow: mvAccent.shadow,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Header del dispositivo */}
                <div
                  onClick={() => onOpenGaugePanel(device)}
                  style={{
                    padding: '1rem 1.25rem',
                    background: `linear-gradient(90deg, ${baseColor}20 0%, transparent 100%)`,
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: mvAccent.primary,
                      boxShadow: `0 0 8px ${mvAccent.primary}`
                    }} />
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: colors.text }}>
                        {device.name}
                      </h4>
                      <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>
                        {device.site?.name || 'Sin sitio'} • {magnitudes.length} {magnitudes.length === 1 ? 'métrica' : 'métricas'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenHistory(device._id);
                      }}
                      style={{
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '8px',
                        padding: '0.4rem 0.75rem',
                        color: '#60a5fa',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        transition: 'all 0.2s ease'
                      }}
                      title="Ver historial"
                    >
                      📈 Historial
                    </button>
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      background: mvAccent.badgeBg,
                      color: mvAccent.primary,
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {mvAccent.label}
                    </span>
                  </div>
                </div>

                {/* Métricas del dispositivo */}
                <div style={{
                  padding: '1rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.75rem'
                }}>
                  {magnitudes.map((mag, magIndex) => (
                    <MetricCard
                      key={`${device._id}_${mag.name}`}
                      magnitude={mag}
                      historyKey={`${device._id}_${mag.name}`}
                      color={metricColors[(deviceIndex + magIndex) % metricColors.length]}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MetricsView;
